import styled from "styled-components";
import theme from "../../style/theme";
import { RoutingType } from "../../types/routing-service/routing-request";
import { CarType, VehiclePayload } from "../../types/routing-service/vehicle";
import { RoutingServicePostCodeButton } from "../button/RoutingServicePostCodeButton";
import { RoutingServiceCheckBox } from "../checkbox/RoutingServiceCheckBox";
import { RoutingServiceTime2 } from "../date/RoutingServiceTime2";
import AddIcon from "../icons/AddIcon";
import DeleteIcon from "../icons/DeleteIcon";
import { CarTypeSelector } from "../selector/CarTypeSelector";
import {
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@mui/icons-material/DragHandle';

interface Props {
    index: number;
    vehicle: VehiclePayload;
    routingType: RoutingType;
    editVehicle: (index: number, vehicle: VehiclePayload) => void;
    showAdd: boolean;
    showDelete: boolean;
    clickAdd: () => void;
    clickDelete: (index: number) => void;
}

export const RoutingServiceVehicleTableRow: React.FC<Props> = (
    {
        index,
        vehicle,
        routingType,
        editVehicle,
        showAdd,
        showDelete,
        clickAdd,
        clickDelete,
    }
) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: vehicle.vehicleName });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const carMap = {
        [CarType.Ray]: "레이(100)",
        [CarType.Starex]: "스타렉스(150)",
    };

    const editCarType = (carType: CarType) => {
        editVehicle(index, {
            ...vehicle,
            car: {
                ...vehicle.car,
                carType,
            },
        });
    };

    const editStartPlace = (
        placeName: string,
        latitude: number,
        longitude: number
    ) => {
        editVehicle(index, {
            ...vehicle,
            startPlace: {
                placeName,
                road: "",
                latitude,
                longitude,
            },
        });
    };

    const editStartTime = (hour: string, minute: string) => {
        editVehicle(index, {
            ...vehicle,
            workTimeWindow: {
                ...vehicle.workTimeWindow,
                startTimeLimited: {
                    hour,
                    minute,
                },
            },
        });
    };

    const editEndTime = (hour: string, minute: string) => {
        editVehicle(index, {
            ...vehicle,
            workTimeWindow: {
                ...vehicle.workTimeWindow,
                endTimeLimited: {
                    hour,
                    minute,
                },
            },
        });
    };

    const editEndPlace = (
        placeName: string,
        latitude: number,
        longitude: number
    ) => {
        editVehicle(index, {
            ...vehicle,
            endPlace: {
                placeName,
                road: "",
                latitude,
                longitude,
            },
        });
    };

    const toggleEndPlaceUndefined = () => {
        if (routingType === RoutingType.RouteSequential) {
            return;
        }
        if (vehicle.endPlace === undefined) {
            editVehicle(index, {
                ...vehicle,
                endPlace: {
                    placeName: "",
                    road: "",
                    latitude: 0,
                    longitude: 0,
                },
            });
        } else {
            editVehicle(index, {
                ...vehicle,
                endPlace: undefined,
            });
        }
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableData>
                <Container>
                    <DragHandle {...attributes} {...listeners}>
                        <DragHandleIcon />
                    </DragHandle>
                    <NameContainer>{vehicle.vehicleName}</NameContainer>
                </Container>
            </TableData>
            <TableData>
                <Container>
                    <RoutingServiceTime2
                        time={vehicle.workTimeWindow.startTimeLimited}
                        editTime={editStartTime}
                    />
                </Container>
            </TableData>
            <TableData>
                <Container>
                    <RoutingServiceTime2
                        time={vehicle.workTimeWindow.endTimeLimited}
                        editTime={editEndTime}
                    />
                </Container>
            </TableData>
            <TableData>
                <Container>
                    <CarTypeSelector
                        candidateMap={carMap}
                        value={vehicle.car.carType}
                        onClickItem={editCarType}
                    />
                </Container>
            </TableData>
            <TableData>
                <Container>
                    <RoutingServicePostCodeButton
                        text={vehicle.startPlace.placeName}
                        disabled={false}
                        setAddress={(address: string) => {
                            editStartPlace(address, 0, 0);
                        }}
                    />
                </Container>
            </TableData>
            <TableData>
                <EndContainer>
                    <EndLocationContainer>
                        <RoutingServicePostCodeButton
                            text={vehicle.endPlace?.placeName}
                            disabled={vehicle.endPlace === undefined}
                            setAddress={(address: string) => {
                                editEndPlace(address, 0, 0);
                            }}
                        />
                        <RoutingServiceCheckBox
                            checked={vehicle.endPlace === undefined}
                            text1={"설정 안함"}
                            text2={"- *최적 배차 종료지로 자동 입력"}
                            disabled={routingType === RoutingType.RouteSequential}
                            onClick={toggleEndPlaceUndefined}
                        />
                        <ButtonContent>
                            {showDelete && (
                                <div onClick={() => clickDelete(index)}>
                                    <DeleteIcon />
                                </div>
                            )}
                            {showAdd && (
                                <div onClick={clickAdd}>
                                    <AddIcon />
                                </div>
                            )}
                        </ButtonContent>
                    </EndLocationContainer>
                </EndContainer>
            </TableData>
        </TableRow>
    );
};

const TableRow = styled.tr`
  & > td:first-child {
    border-top: 1px solid ${theme.palette.primitives.gray200};
    border-left: 1px solid ${theme.palette.primitives.gray200};
    border-bottom: 1px solid ${theme.palette.primitives.gray200};
    box-sizing: border-box;
    border-radius: 12px 0 0 12px;
  }

  & > td:last-child {
    border-top: 1px solid ${theme.palette.primitives.gray200};
    border-right: 1px solid ${theme.palette.primitives.gray200};
    border-bottom: 1px solid ${theme.palette.primitives.gray200};
    box-sizing: border-box;
    border-radius: 0 12px 12px 0;
  }
`;

const TableData = styled.td`
  width: fit-content;
  height: fit-content;
  border-top: 1px solid ${theme.palette.primitives.gray200};
  border-bottom: 1px solid ${theme.palette.primitives.gray200};
  padding: 0;
  margin: 0;
`;

const Container = styled.div`
  width: fit-content;
  height: max-content;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 18px 2px 20px 2px;
  margin: 0 4px 0 16px;
  box-sizing: border-box;
  gap: 8px;
`;

const EndContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 18px 2px 20px 2px;
  margin: 0 16px 0 16px;
  box-sizing: border-box;
`;

const NameContainer = styled.div`
  width: max-content;
  height: 41px;
  box-sizing: border-box;
  padding: 11px 0 11px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: ${theme.palette.primitives.gray800};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const EndLocationContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const ButtonContent = styled.div`
  display: flex;
  height: 56px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  position: absolute;
  top: 8px;
  left: 300px;
  padding: 0 34px 0 0;
  box-sizing: border-box;
`;

const DragHandle = styled.div`
  cursor: move;
  display: flex;
  align-items: center;
  padding: 0 8px;
  color: ${theme.palette.primitives.gray400};
  
  &:hover {
    color: ${theme.palette.primitives.gray600};
  }
`;
